import { memo, useCallback, useState } from "react";
import PropTypes from "prop-types";
import { Modal, Button } from "antd";
import { CheckCircleFilled } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { postApiRequest } from "../../../services/postApiRequest";
import { END_POINTS } from "../../../Helper/Constant";
import { VIP_PRICE_COINS } from "../../vip/vipConstants";
import { setUser } from "../../../features/user/userSlice";
import { getLocalStorageData, setLocalStorageData } from "../../commonFunction";
import { showToast } from "../ToasterMessage/ToasterMessage";
import coinIcon from "../../../assets/icons/header-coin.svg";
import "./VipJoinModal.scss";

const VIP_FEATURES = [
  "vip.feature_messages",
  "vip.feature_list",
  "vip.feature_border",
];

const VipJoinModal = ({ open, onClose, onJoined }) => {
  const { t } = useTranslation("common");
  const dispatch = useDispatch();
  const userData =
    useSelector((state) => state.user.userData) ||
    JSON.parse(getLocalStorageData("user") || "{}");
  const [joining, setJoining] = useState(false);

  const handleJoin = useCallback(async () => {
    if (joining) return;
    setJoining(true);
    try {
      const response = await postApiRequest(END_POINTS.VIP_JOIN, { isVip: true });
      if (response && response.success !== false) {
        const updatedUser =
          response.user ||
          response.updatedUser ||
          {
            ...userData,
            isVip: true,
            totalCoins: Math.max(0, (userData?.totalCoins || 0) - VIP_PRICE_COINS),
          };
        dispatch(setUser(updatedUser));
        setLocalStorageData("user", JSON.stringify(updatedUser));
        showToast(response?.message || t("vip.join_success"), "success");
        onJoined?.();
        onClose();
      }
    } catch (error) {
      showToast(
        error?.response?.data?.message || error?.error?.message || error?.message,
        "error"
      );
    } finally {
      setJoining(false);
    }
  }, [dispatch, joining, onClose, onJoined, t, userData]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      closable
      title={null}
      width={380}
      className="vip-join-modal"
    >
      <div className="vip-join-modal__icon" aria-hidden="true">
        <span className="vip-join-modal__star">★</span>
      </div>

      <h2 className="vip-join-modal__title">{t("vip.join_title")}</h2>
      <p className="vip-join-modal__desc">{t("vip.join_description")}</p>

      <div className="vip-join-modal__price">
        <span>{t("vip.join_price")}</span>
        <img src={coinIcon} alt="" className="vip-join-modal__coin" />
      </div>

      <ul className="vip-join-modal__features">
        {VIP_FEATURES.map((key) => (
          <li key={key}>
            <CheckCircleFilled className="vip-join-modal__check" />
            <span>{t(key)}</span>
          </li>
        ))}
      </ul>

      <Button
        type="primary"
        className="vip-join-modal__btn"
        loading={joining}
        onClick={handleJoin}
      >
        {t("vip.join_button")}
      </Button>
    </Modal>
  );
};

VipJoinModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onJoined: PropTypes.func,
};

export default memo(VipJoinModal);
